// Free camera: WASD move, mouse wheel zoom, right-drag rotate, middle-drag pan.
import * as THREE from 'three';

export class FreeCamera {
  constructor(camera, domElement, terrain) {
    this.camera = camera;
    this.dom = domElement;
    this.terrain = terrain;

    this.target = new THREE.Vector3(0, terrain ? terrain.heightAtWorld(0, 0) : 0, 0);
    this.distance = 90;
    this.yaw = Math.PI * 0.25;
    this.pitch = 0.9;               // radians from horizontal
    this.minPitch = 0.15;
    this.maxPitch = 1.45;
    this.minDist = 12;
    this.maxDist = 240;

    this.keys = new Set();
    this._dragging = null;          // 'rotate' | 'pan'
    this._last = { x: 0, y: 0 };

    this._bind();
    this.update(0);
  }

  _bind() {
    this._onKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      this.keys.add(e.code);
    };
    this._onKeyUp = (e) => this.keys.delete(e.code);
    this._onBlur = () => this.keys.clear();
    this._onContextMenu = (e) => e.preventDefault();
    this._onPointerDown = (e) => {
      if (e.button === 2) this._dragging = 'rotate';
      else if (e.button === 1) this._dragging = 'pan';
      this._last = { x: e.clientX, y: e.clientY };
      this.dom.setPointerCapture(e.pointerId);
    };
    this._onPointerUp = () => { this._dragging = null; };
    this._onPointerMove = (e) => {
      if (!this._dragging) return;
      const dx = e.clientX - this._last.x;
      const dy = e.clientY - this._last.y;
      this._last = { x: e.clientX, y: e.clientY };
      if (this._dragging === 'rotate') {
        this.yaw -= dx * 0.005;
        this.pitch = THREE.MathUtils.clamp(this.pitch + dy * 0.004, this.minPitch, this.maxPitch);
      } else {
        const panSpeed = this.distance * 0.0015;
        const forward = new THREE.Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw));
        const right = new THREE.Vector3(forward.z, 0, -forward.x);
        this.target.addScaledVector(right, dx * panSpeed);
        this.target.addScaledVector(forward, dy * panSpeed);
      }
    };
    this._onWheel = (e) => {
      e.preventDefault();
      this.distance = THREE.MathUtils.clamp(this.distance * (1 + Math.sign(e.deltaY) * 0.12), this.minDist, this.maxDist);
    };

    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('blur', this._onBlur);
    this.dom.addEventListener('contextmenu', this._onContextMenu);
    this.dom.addEventListener('pointerdown', this._onPointerDown);
    this.dom.addEventListener('pointerup', this._onPointerUp);
    this.dom.addEventListener('pointermove', this._onPointerMove);
    this.dom.addEventListener('wheel', this._onWheel, { passive: false });
  }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('blur', this._onBlur);
    this.dom.removeEventListener('contextmenu', this._onContextMenu);
    this.dom.removeEventListener('pointerdown', this._onPointerDown);
    this.dom.removeEventListener('pointerup', this._onPointerUp);
    this.dom.removeEventListener('pointermove', this._onPointerMove);
    this.dom.removeEventListener('wheel', this._onWheel);
    this.keys.clear();
    this._dragging = null;
  }

  update(dt) {
    // WASD movement relative to camera yaw
    const speed = this.distance * 0.9 * dt;
    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const right = new THREE.Vector3(-forward.z, 0, forward.x);
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) this.target.addScaledVector(forward, speed);
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) this.target.addScaledVector(forward, -speed);
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) this.target.addScaledVector(right, -speed);
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) this.target.addScaledVector(right, speed);
    if (this.keys.has('KeyQ')) this.yaw += dt * 1.5;
    if (this.keys.has('KeyE')) this.yaw -= dt * 1.5;

    if (this.terrain) {
      const half = this.terrain.worldSize / 2 - 4;
      this.target.x = THREE.MathUtils.clamp(this.target.x, -half, half);
      this.target.z = THREE.MathUtils.clamp(this.target.z, -half, half);
      this.target.y = this.terrain.heightAtWorld(this.target.x, this.target.z);
    }

    const offset = new THREE.Vector3(
      Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      Math.cos(this.yaw) * Math.cos(this.pitch)
    ).multiplyScalar(this.distance);
    this.camera.position.copy(this.target).add(offset);

    // Keep camera above terrain
    if (this.terrain) {
      const minY = this.terrain.heightAtWorld(this.camera.position.x, this.camera.position.z) + 3;
      if (this.camera.position.y < minY) this.camera.position.y = minY;
    }
    this.camera.lookAt(this.target);
  }
}
